import moment from "moment";
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Label,
  Pagination,
  Select,
  Spinner,
  Table,
  TextInput,
} from "flowbite-react";
import { LuCalendarClock } from "react-icons/lu";
import {
  HiDownload,
  HiExternalLink,
  HiOutlineAdjustments,
  HiOutlineChartBar,
  HiOutlineCash,
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
  HiOutlineRefresh,
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineX,
  HiSearch,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { ADMIN, PAYMENTS } from "../../config/apiConfig";
import LastMonthsSelect from "../inputs/LastMonthsSelect";
import { formatToReal } from "../../utils/utils";
import EmptyTable from "./EmptyTable";
import { FaArrowsLeftRight } from "react-icons/fa6";

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

const DENSITIES = {
  comfy: { row: "py-3", cell: "py-3 px-3 text-sm" },
  compact: { row: "py-1.5", cell: "py-1.5 px-2 text-xs" },
};
const ACTION_BTN_BASE =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shadow-sm transition";
const ACTION_BTN_PAY =
  "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300";
const ACTION_BTN_ATTACH =
  "border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:border-slate-300";

function StatusDot({ color = "bg-gray-300" }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function exportCSV(rows, filename = "invoices.csv") {
  if (!rows?.length) return;
  const headers = [
    "id",
    "createdAt",
    "dueDate",
    "paidAt",
    "price",
    "type",
    "status",
    "paymentLink",
  ];
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.id ?? "",
        r.createdAt ? moment(r.createdAt).format("YYYY-MM-DD HH:mm:ss") : "",
        r.dueDate ? moment(r.dueDate).format("YYYY-MM-DD") : "",
        r.updatedAt ? moment(r.updatedAt).format("YYYY-MM-DD HH:mm:ss") : "",
        r.price ?? "",
        r.type ?? "",
        r.status ?? (r.isPaid ? "paid" : "pending"),
        r.paymentLink ?? "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const InvoicesTable = () => {
  const [period, setPeriod] = useState(moment().format("YYYY-MM"));
  const [showFilters, setShowFilters] = useState(true);

  // filtros locais
  const [methodFilter, setMethodFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false); // apenas pendentes
  const [downloadError, setDownloadError] = useState("");

  // densidade
  const [dense, setDense] = useState(false);

  // paginação local
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);

  // ===== Queries das listas =====
  const {
    isLoading: pendingLoading,
    error: pendingError,
    data: pendingInvoices,
    refetch: refetchPending,
  } = useQuery(["invoices", period, "pending"], async () => {
    const { data } = await axios.get(
      `${PAYMENTS.GET_ALL}?period=${period}&isPaid=false`
    );
    return data?.data?.data || [];
  });

  const {
    isLoading: paidLoading,
    error: paidError,
    data: paidInvoices,
    refetch: refetchPaid,
  } = useQuery(["invoices", period, "paid"], async () => {
    const { data } = await axios.get(
      `${PAYMENTS.GET_ALL}?period=${period}&isPaid=true`
    );
    return data?.data?.data || [];
  });

  const pendingCount = pendingInvoices?.length || 0;
  const paidCount = paidInvoices?.length || 0;

  // ===== Métodos únicos (para filtro) =====
  const availableMethods = useMemo(() => {
    const base = [...(pendingInvoices || []), ...(paidInvoices || [])];
    const set = new Set(base.map((i) => i?.type).filter(Boolean));
    return ["", ...Array.from(set)];
  }, [pendingInvoices, paidInvoices]);

  const filterList = (list) => {
    const term = (searchTerm || "").trim().toLowerCase();
    const min = minValue ? parseFloat(minValue.replace(",", ".")) : null;
    const max = maxValue ? parseFloat(maxValue.replace(",", ".")) : null;

    return (list || []).filter((inv) => {
      const byMethod = methodFilter ? inv?.type === methodFilter : true;
      const bySearch = term
        ? [inv?.id, inv?.type, inv?.paymentLink]
            .map((v) => (v ? String(v).toLowerCase() : ""))
            .some((v) => v.includes(term))
        : true;
      const price = Number(inv?.price ?? 0);
      const byMin = min !== null ? price >= min : true;
      const byMax = max !== null ? price <= max : true;
      return byMethod && bySearch && byMin && byMax;
    });
  };

  const pendingWithFlag = useMemo(
    () => (pendingInvoices || []).map((inv) => ({ ...inv, __isPaid: false })),
    [pendingInvoices]
  );
  const paidWithFlag = useMemo(
    () => (paidInvoices || []).map((inv) => ({ ...inv, __isPaid: true })),
    [paidInvoices]
  );

  // ===== Filtragem local =====
  const filteredPending = useMemo(
    () => filterList(pendingWithFlag),
    [pendingWithFlag, methodFilter, searchTerm, minValue, maxValue]
  );
  const filteredPaid = useMemo(
    () => filterList(paidWithFlag),
    [paidWithFlag, methodFilter, searchTerm, minValue, maxValue]
  );

  // ===== Filtro "somente atrasados" (apenas pendentes) =====
  const filteredPendingWithOverdue = useMemo(() => {
    if (!onlyOverdue) return filteredPending;
    const today = moment().startOf("day");
    return filteredPending.filter(
      (it) => it.dueDate && moment(it.dueDate).isBefore(today, "day")
    );
  }, [filteredPending, onlyOverdue]);

  const combinedFiltered = useMemo(() => {
    if (onlyOverdue) return filteredPendingWithOverdue;
    return [...filteredPendingWithOverdue, ...filteredPaid];
  }, [filteredPendingWithOverdue, filteredPaid, onlyOverdue]);

  // ===== Paginação local =====
  const totalItems = combinedFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return combinedFiltered.slice(start, start + itemsPerPage);
  }, [combinedFiltered, currentPage, itemsPerPage]);

  // ===== Resumo (cards) =====
  const summary = useMemo(() => {
    const arr = combinedFiltered;
    const total = arr.reduce((acc, it) => acc + Number(it.price || 0), 0);
    const qty = arr.length;
    const avg = qty ? total / qty : 0;
    const today = moment().startOf("day");
    const vencidos = filteredPending.filter(
      (it) => it.dueDate && moment(it.dueDate).isBefore(today, "day")
    ).length;
    return { total, qty, avg, vencidos };
  }, [combinedFiltered, filteredPending]);

  // ===== Helpers =====
  const dueBadge = (dueDate) => {
    if (!dueDate) return <div>-</div>;
    const today = moment().startOf("day");
    const due = moment(dueDate).startOf("day");

    if (due.isSame(today)) {
      return (
        <Badge color="warning" className="w-fit mx-auto">
          Vence hoje
        </Badge>
      );
    }
    if (due.isBefore(today)) {
      const diff = today.diff(due, "days");
      return (
        <Badge color="failure" className="w-fit mx-auto">
          Vencido há {diff} {diff === 1 ? "dia" : "dias"}
        </Badge>
      );
    }
    const diff = due.diff(today, "days");
    return (
      <Badge color="success" className="w-fit mx-auto">
        Vence em {diff} {diff === 1 ? "dia" : "dias"}
      </Badge>
    );
  };

  const getAttachmentInfo = (invoice) => {
    const attachmentId =
      invoice?.attachmentFileId || invoice?.attachment?.id || null;
    const attachmentUrl =
      invoice?.attachmentUrl ||
      invoice?.attachment?.publicUrl ||
      invoice?.attachment?.url ||
      null;
    return { attachmentId, attachmentUrl };
  };

  const handleDownloadAttachment = async (invoice) => {
    const { attachmentId, attachmentUrl } = getAttachmentInfo(invoice);
    if (attachmentUrl) {
      window.open(attachmentUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (!attachmentId || !invoice?.companyId) return;
    setDownloadError("");
    try {
      const { data } = await axios.get(
        `${ADMIN.COMPANY_FILES_BASE}/${invoice.companyId}/files/${attachmentId}`
      );
      const url = data?.url || data?.data?.url;
      if (!url) throw new Error("URL ausente");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setDownloadError("Não foi possível baixar o anexo.");
    }
  };

  const resetFilters = () => {
    setMethodFilter("");
    setSearchTerm("");
    setMinValue("");
    setMaxValue("");
    setOnlyOverdue(false);
    setItemsPerPage(ITEMS_PER_PAGE_OPTIONS[0]);
    setPage(1);
    refetchPending();
    refetchPaid();
  };

  const rowCls = dense ? DENSITIES.compact.row : DENSITIES.comfy.row;
  const cellCls = dense ? DENSITIES.compact.cell : DENSITIES.comfy.cell;
  const monthLabel = moment(period, "YYYY-MM").format("MMM/YYYY");
  const activeFiltersCount = [
    methodFilter,
    searchTerm,
    minValue,
    maxValue,
    onlyOverdue,
  ].filter(Boolean).length;
  const handleExport = () => {
    exportCSV(combinedFiltered, `faturas-${period}-todas.csv`);
  };
  return (
    <div className="flex flex-col gap-4">
      {/* Filtros próprios */}
      {showFilters && (
        <div className="w-full bg-white/95 rounded-xl shadow-sm p-4 border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-8 gap-3">
            <div className="flex flex-col md:col-span-2">
              <Label className="text-xs mb-1" value="Mês" />
              <LastMonthsSelect
                value={period}
                onChange={(e) => {
                  setPeriod(e.target.value);
                  setPage(1);
                }}
                className="w-full"
              />
            </div>

            <div className="flex flex-col md:col-span-4">
              <Label
                className="text-xs mb-1"
                value="Buscar (ID, link, método…)"
              />
              <div className="flex gap-2">
                <TextInput
                  icon={HiSearch}
                  placeholder="Digite para filtrar…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Buscar faturas"
                  className="w-full"
                />
                {searchTerm && (
                  <Button
                    color="light"
                    onClick={() => setSearchTerm("")}
                    title="Limpar busca"
                  >
                    <HiOutlineX />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-col md:col-span-2">
              <Label className="text-xs mb-1" value="Método" />
              <Select
                value={methodFilter}
                onChange={(e) => {
                  setMethodFilter(e.target.value);
                  setPage(1);
                }}
              >
                {availableMethods.map((m, i) => (
                  <option key={i} value={m}>
                    {m || "Todos"}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Chips de filtros ativos */}
          {(methodFilter ||
            minValue ||
            maxValue ||
            searchTerm ||
            onlyOverdue) && (
            <div className="flex flex-wrap gap-2 pt-3">
              {methodFilter && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Método: {methodFilter}
                  <button
                    onClick={() => setMethodFilter("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {minValue && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Min: {minValue}
                  <button
                    onClick={() => setMinValue("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {maxValue && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Máx: {maxValue}
                  <button
                    onClick={() => setMaxValue("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Busca: “{searchTerm}”
                  <button
                    onClick={() => setSearchTerm("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {onlyOverdue && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Somente atrasados
                  <button
                    onClick={() => setOnlyOverdue(false)}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative overflow-hidden rounded-xl p-4 shadow-sm border border-slate-200 bg-primary text-white">
          <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-white/10" />
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wide text-white/60">
              Total
            </div>
            <HiOutlineCash className="text-lg text-white/70" />
          </div>
          <div className="text-xl font-semibold mt-2">
            {formatToReal(summary.total)}
          </div>
        </div>
        <div className="rounded-xl p-4 shadow-sm border border-slate-200 bg-white">
          <div className="flex items-center justify-between text-xs uppercase tracking-wide text-slate-500">
            Quantidade
            <HiOutlineDocumentText className="text-lg text-slate-400" />
          </div>
          <div className="text-xl font-semibold mt-2">{summary.qty}</div>
        </div>
        <div className="rounded-xl p-4 shadow-sm border border-rose-100 bg-rose-50">
          <div className="flex items-center justify-between text-xs uppercase tracking-wide text-rose-600">
            Vencidos
            <HiOutlineExclamationCircle className="text-lg text-rose-500" />
          </div>
          <div className="text-xl font-semibold mt-2 text-rose-700">
            {summary.vencidos}
          </div>
        </div>
      </div>

      {/* Lista única */}
      <div className="bg-white p-4  shadow-sm rounded-xl border border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">
              Faturas
            </span>
            <span className="rounded-full bg-slate-100 text-slate-700 text-xs px-2 py-0.5">
              {totalItems}
            </span>
          </div>
          {onlyOverdue && (
            <span className="text-xs text-rose-600">Somente atrasados</span>
          )}
        </div>

        {pendingError || paidError ? (
          <Alert color="failure" className="mt-2">
            <span className="font-medium">Erro:</span> Não foi possível carregar
            os dados.
            <Button
              size="xs"
              color="failure"
              className="ml-2"
              onClick={() => {
                refetchPending();
                refetchPaid();
              }}
            >
              Tentar novamente
            </Button>
          </Alert>
        ) : pendingLoading || paidLoading ? (
          <div className="mt-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <Spinner size="sm" />
                <div className="h-3 bg-gray-200 rounded-full w-1/3" />
                <div className="h-3 bg-gray-200 rounded-full w-1/4" />
                <div className="h-3 bg-gray-200 rounded-full w-1/6" />
              </div>
            ))}
          </div>
        ) : combinedFiltered.length > 0 ? (
          <>
            {/* Listagem mobile */}
            <div className="md:hidden space-y-3 mt-2">
              {paged.map((invoice, index) => {
                const isOverdue =
                  !invoice.__isPaid &&
                  invoice.dueDate &&
                  moment(invoice.dueDate)
                    .startOf("day")
                    .isBefore(moment().startOf("day"));
                const statusLabel = invoice.__isPaid
                  ? "Pago"
                  : isOverdue
                  ? "Atrasado"
                  : "Aberto";
                const { attachmentId, attachmentUrl } =
                  getAttachmentInfo(invoice);
                const hasAttachment = Boolean(
                  attachmentId || attachmentUrl
                );
                return (
                  <div
                    key={invoice.id ?? index}
                    className="border border-slate-200 rounded-xl p-3 bg-white shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs text-slate-500">Criação</div>
                        <div className="text-sm font-medium text-slate-800">
                          {invoice.createdAt
                            ? moment(invoice.createdAt).format("DD/MM/YYYY")
                            : "-"}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs ${
                          invoice.__isPaid
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : isOverdue
                            ? "border-rose-200 bg-rose-50 text-rose-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                      >
                        <StatusDot
                          color={
                            invoice.__isPaid
                              ? "bg-emerald-500"
                              : isOverdue
                              ? "bg-rose-500"
                              : "bg-amber-400"
                          }
                        />
                        {statusLabel}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <div className="flex flex-col items-start">
                        <div className="text-xs text-slate-500">
                          Vencimento / Pago
                        </div>
                        <div className="mt-1">
                          {invoice.__isPaid
                            ? invoice.updatedAt
                              ? moment(invoice.updatedAt).format("DD/MM/YYYY")
                              : "-"
                            : dueBadge(invoice.dueDate)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Valor</div>
                        <div className="text-sm font-semibold text-slate-900">
                          {formatToReal(invoice.price)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {invoice.type || "-"}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      {hasAttachment && (
                        <button
                          className={`${ACTION_BTN_BASE} ${ACTION_BTN_ATTACH}`}
                          onClick={() => handleDownloadAttachment(invoice)}
                        >
                          <HiDownload />
                          Anexo
                        </button>
                      )}
                      {!invoice.__isPaid && invoice.paymentLink ? (
                        <a
                          className={`${ACTION_BTN_BASE} ${ACTION_BTN_PAY}`}
                          href={invoice.paymentLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <HiExternalLink />
                          Pagar
                        </a>
                      ) : (
                        !hasAttachment && (
                          <span className="text-gray-400 text-xs">—</span>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tabela desktop */}
            <div className="hidden md:block overflow-x-auto rounded-lg mt-2 border border-gray-200">
              <Table className="min-w-[720px]">
                <Table.Head className="sticky top-0 z-10">
                  <Table.HeadCell className="bg-slate-50 text-center text-xs uppercase tracking-wide text-slate-500">
                    Criação
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50 text-center text-xs uppercase tracking-wide text-slate-500">
                    Vencimento / Pago em
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50 text-right text-xs uppercase tracking-wide text-slate-500">
                    Valor
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50 text-center text-xs uppercase tracking-wide text-slate-500">
                    Método
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50 text-center text-xs uppercase tracking-wide text-slate-500">
                    Status
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50 text-center text-xs uppercase tracking-wide text-slate-500">
                    Ações
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y divide-slate-100">
                  {paged.map((invoice, index) => {
                    const isOverdue =
                      !invoice.__isPaid &&
                      invoice.dueDate &&
                      moment(invoice.dueDate)
                        .startOf("day")
                        .isBefore(moment().startOf("day"));
                    const statusLabel = invoice.__isPaid
                      ? "Pago"
                      : isOverdue
                      ? "Atrasado"
                      : "Aberto";
                    const { attachmentId, attachmentUrl } =
                      getAttachmentInfo(invoice);
                    const hasAttachment = Boolean(
                      attachmentId || attachmentUrl
                    );
                    return (
                      <Table.Row
                        key={invoice.id ?? index}
                        className={`hover:bg-gray-50 ${rowCls}`}
                      >
                        <Table.Cell className={`text-center ${cellCls}`}>
                          {invoice.createdAt
                            ? moment(invoice.createdAt).format("DD/MM/YYYY")
                            : "-"}
                        </Table.Cell>
                        <Table.Cell className={`text-center ${cellCls}`}>
                          {invoice.__isPaid
                            ? invoice.updatedAt
                              ? moment(invoice.updatedAt).format("DD/MM/YYYY")
                              : "-"
                            : dueBadge(invoice.dueDate)}
                        </Table.Cell>
                        <Table.Cell
                          className={`text-right font-medium ${cellCls}`}
                        >
                          {formatToReal(invoice.price)}
                        </Table.Cell>
                        <Table.Cell className={`text-center ${cellCls}`}>
                          {invoice.type || "-"}
                        </Table.Cell>
                        <Table.Cell className={`text-center ${cellCls}`}>
                          <span
                            className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs ${
                              invoice.__isPaid
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : isOverdue
                                ? "border-rose-200 bg-rose-50 text-rose-700"
                                : "border-amber-200 bg-amber-50 text-amber-700"
                            }`}
                          >
                            <StatusDot
                              color={
                                invoice.__isPaid
                                  ? "bg-emerald-500"
                                  : isOverdue
                                  ? "bg-rose-500"
                                  : "bg-amber-400"
                              }
                            />
                            {statusLabel}
                          </span>
                        </Table.Cell>
                        <Table.Cell className={`text-center ${cellCls}`}>
                          <div className="inline-flex items-center justify-center gap-2">
                            {hasAttachment && (
                              <button
                                className={`${ACTION_BTN_BASE} ${ACTION_BTN_ATTACH}`}
                                onClick={() => handleDownloadAttachment(invoice)}
                              >
                                <HiDownload />
                                Anexo
                              </button>
                            )}
                            {!invoice.__isPaid && invoice.paymentLink ? (
                              <a
                                className={`${ACTION_BTN_BASE} ${ACTION_BTN_PAY}`}
                                href={invoice.paymentLink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <HiExternalLink />
                                Pagar
                              </a>
                            ) : (
                              !hasAttachment && (
                                <span className="text-gray-400">—</span>
                              )
                            )}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>

            {downloadError && (
              <Alert color="failure" className="mt-3">
                {downloadError}
              </Alert>
            )}

            {/* Paginação */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-3">
              <div className="text-xs text-gray-500">
                Mostrando {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(currentPage * itemsPerPage, totalItems)} de{" "}
                {totalItems}
              </div>
              <Pagination
                currentPage={currentPage}
                onPageChange={setPage}
                previousLabel="Anterior"
                nextLabel="Próximo"
                totalPages={totalPages}
              />
            </div>
          </>
        ) : (
          <EmptyTable icon="search_off">Nenhuma fatura encontrada.</EmptyTable>
        )}
      </div>
    </div>
  );
};

export default InvoicesTable;
